import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { filterActiveQuoteLines } from 'src/shared/utils/quoteAlternatives'
import { calculateQuoteFooterSubtotals, calculateQuoteTotal } from './utils'

const DEBOUNCE_DELAY = 500

export const useDebouncedFieldUpdate = <T,>(
  initialValue: T,
  onUpdate: (value: T) => void,
  deps: unknown[] = []
) => {
  const [value, setValue] = useState(initialValue)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleChange = useCallback(
    (newValue: T) => {
      setValue(newValue)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => onUpdate(newValue), DEBOUNCE_DELAY)
    },
    [onUpdate, ...deps]
  )

  return [value, setValue, handleChange] as const
}

export const useOrderTableData = ({
  quote_id,
  quoteItems: quoteItemsProp,
}: {
  quote_id?: number
  quoteItems?: QuoteItem_[]
}) => {
  const { quoteItems: quoteItemsQuery, createQuoteItem } = useQuoteItemApi({
    quote_id: quoteItemsProp ? undefined : quote_id!,
  })

  const quoteItems = quoteItemsProp
    ? { data: quoteItemsProp, isPending: false }
    : quoteItemsQuery

  // Proposal alternatives stay on the quote; only the active one per slot counts.
  const countedItems = filterActiveQuoteLines(quoteItems.data)
  const total_price = calculateQuoteTotal(countedItems)
  const footerSubtotals = calculateQuoteFooterSubtotals(countedItems)

  return { quoteItems, total_price, footerSubtotals, createQuoteItem }
}
